import * as React from 'react'
import { Check, Plus, Trash2 } from 'lucide-react'
import { usePutOwnMediaLinks } from '@/hooks/useVendors'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { FormActions } from '@/components/ui/form-actions'
import { Input } from '@/components/ui/input'
import { FormError } from '@/components/ui/form-error'
import { mediaKindOf } from '@lib/helpers/vendor-media'
import { VENDOR_MEDIA_KIND_LABELS } from '@lib/enums/vendor-media-kind.enum'
import { MAX_VENDOR_MEDIA_LINKS } from '@lib/constants/vendor-profile'
import { putVendorMediaLinksInputSchema } from '@lib/input/vendor-profile.input'
import type { OwnVendorProfileDto } from '@lib/dto/vendor-profile.dto'

interface Row {
  /** Keeps a row's inputs its own while rows above it are removed. */
  key: string
  url: string
  title: string
}

function rowOf(url: string, title: string): Row {
  return { key: crypto.randomUUID(), url, title }
}

/** Which site a typed address is on, or why it can't be saved. */
function siteOf(url: string): React.ReactNode {
  if (url.trim() === '') return null
  const kind = mediaKindOf(url.trim())
  return kind === null ? (
    <span className="text-xs text-danger-ink">Not a YouTube, Vimeo or SoundCloud page</span>
  ) : (
    <Badge tone="info">{VENDOR_MEDIA_KIND_LABELS[kind]}</Badge>
  )
}

/**
 * Videos and mixes: highlight films, a first dance, a DJ set. Linked from
 * YouTube, Vimeo or SoundCloud rather than uploaded, and saved as one list.
 */
export function MediaLinksCard({ profile }: { profile: OwnVendorProfileDto }) {
  const save = usePutOwnMediaLinks()
  const [rows, setRows] = React.useState<Row[]>(() => profile.mediaLinks.map((link) => rowOf(link.url, link.title ?? '')))
  const [error, setError] = React.useState<string | null>(null)
  const [saved, setSaved] = React.useState(false)

  function change(key: string, patch: Partial<Pick<Row, 'url' | 'title'>>): void {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)))
    setSaved(false)
  }

  async function submit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setError(null)
    setSaved(false)
    const parsed = putVendorMediaLinksInputSchema.safeParse({
      links: rows
        .filter((row) => row.url.trim() !== '')
        .map((row) => ({ url: row.url.trim(), title: row.title.trim() === '' ? null : row.title.trim() })),
    })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Check the links.')
      return
    }
    try {
      await save.mutateAsync(parsed.data)
      setSaved(true)
    } catch (e) {
      setError(requestErrorMessage(e))
    }
  }

  return (
    <Card id="media">
      <CardHeader>
        <CardTitle>Videos and audio</CardTitle>
        <CardDescription>Links to your YouTube, Vimeo or SoundCloud: highlight films, mixes, a first dance.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-3" onSubmit={(e) => void submit(e)} noValidate>
          {rows.length === 0 ? <p className="text-sm text-ink-muted">No links yet.</p> : null}
          {rows.map((row, index) => (
            <div key={row.key} className="flex flex-wrap items-center gap-2">
              <Input
                type="url"
                inputMode="url"
                placeholder="https://youtube.com/watch?v=…"
                value={row.url}
                aria-label={`Link ${String(index + 1)}`}
                onChange={(e) => change(row.key, { url: e.target.value })}
                className="min-w-[16rem] flex-1"
              />
              <Input
                placeholder="What it is (optional)"
                value={row.title}
                maxLength={120}
                aria-label={`What link ${String(index + 1)} is`}
                onChange={(e) => change(row.key, { title: e.target.value })}
                className="w-56"
              />
              <span className="w-28">{siteOf(row.url)}</span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={`Remove link ${String(index + 1)}`}
                onClick={() => {
                  setRows((current) => current.filter((r) => r.key !== row.key))
                  setSaved(false)
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={rows.length >= MAX_VENDOR_MEDIA_LINKS}
            onClick={() => setRows((current) => [...current, rowOf('', '')])}
          >
            <Plus className="h-4 w-4" /> Add a link
          </Button>
          {error ? <FormError>{error}</FormError> : null}
          <FormActions>
            {saved ? (
              <span className="inline-flex items-center gap-1 text-sm text-ok-ink">
                <Check className="h-4 w-4" aria-hidden /> Saved.
              </span>
            ) : null}
            <Button type="submit" disabled={save.isPending}>
              Save links
            </Button>
          </FormActions>
        </form>
      </CardContent>
    </Card>
  )
}
